import { merchantKeyOf } from './merchant-key.js'

/**
 * A tabela de referência que categoriza sozinha — `Fase 10`.
 *
 * ## Por que uma entidade própria, e não um campo do estabelecimento
 *
 * [[Merchant]] já tem `patterns` e `defaultCategoryId`, e a tela Classificar já
 * escreve neles. Mas o estabelecimento existe para o catálogo de **preço** —
 * *"onde vi este produto mais barato"* —, e a regra é decisão de **orçamento**.
 * Juntar os dois faria o `never` — "esta descrição nunca recebe categoria
 * sozinha" — morar num objeto que não tem nada a ver com isso, e faria toda
 * regra exigir uma loja, inclusive a de "Transferência enviada|Karen".
 *
 * ## A chave é a do agrupamento, e isso não é coincidência
 *
 * `merchantKeyOf` já resolve o que o extrato da Pluggy faz com a mesma compra:
 * o `1/3` da parcela, o `Dl*` da adquirente, o `Transferência enviada|` na
 * frente da contraparte, o número da loja colado no nome. Ele foi medido contra
 * as 2.272 descrições da casa (decisão 198), e a tela Classificar agrupa por
 * ele. Uma segunda normalização aqui significaria que o grupo que a pessoa
 * decidiu e a regra que nasce da decisão dela poderiam não ser o mesmo conjunto
 * de transações.
 *
 * ## Três estados, e o terceiro é o que evita a pergunta eterna
 *
 * - **Regra com categoria** — aplica.
 * - **Regra `never`** — jamais categoriza sozinho, e não pergunta mais.
 * - **Sem regra** — ainda não se sabe; a próxima categorização à mão pergunta.
 *
 * Sem o `never`, a única forma de calar a pergunta de uma descrição que muda de
 * natureza a cada mês — o Pix para uma pessoa, a transferência que ora é
 * investimento ora é empréstimo — seria categorizá-la errado de propósito.
 */

/** O que fazer quando a chave casa. */
export type RuleDecision =
  | { kind: 'category'; categoryId: string }
  | { kind: 'never' }

/**
 * Onde a regra vale.
 *
 * `transaction` e `item` são espaços diferentes de texto — "uber rides" contra
 * "sacola plastica" —, mas o mesmo nome pode aparecer nos dois: a padaria é uma
 * transação no extrato e uma linha dentro da nota do mercado. `both` é o que
 * permite ensinar uma vez.
 */
export type RuleScope = 'transaction' | 'item' | 'both'

export type CategoryRule = {
  id: string
  householdId: string
  /** `merchantKeyOf` da descrição. Única na casa. */
  key: string
  /** A grafia que deu origem — é o que a pessoa lê na tabela. */
  label: string
  decision: RuleDecision
  origin: 'learned' | 'manual'
  scope: RuleScope
  /** Quantas vezes já categorizou algo. É o peso da regra na tela. */
  appliedCount: number
  lastAppliedAt: string | null
  createdAt: string
  updatedAt: string
}

/** A chave de uma descrição de extrato. Vazia quando não sobra nada dela. */
export function chaveDaDescricao(description: string): string {
  return merchantKeyOf(description)
}

/**
 * A regra que vale para esta chave, neste escopo.
 *
 * **Chave vazia não casa com nada.** `merchantKeyOf` devolve vazio quando a
 * descrição é só número, e uma regra de chave vazia casaria com todas elas —
 * que é o jeito mais rápido de categorizar errado em massa.
 */
export function regraQueCasa(
  chave: string,
  regras: readonly CategoryRule[],
  escopo: Exclude<RuleScope, 'both'>,
): CategoryRule | null {
  if (chave === '') return null
  return (
    regras.find((r) => r.key === chave && (r.scope === escopo || r.scope === 'both')) ?? null
  )
}

/**
 * A categoria que a tabela aplicaria a esta descrição — ou `null`, que quer
 * dizer "deixe como está".
 *
 * `never` e "sem regra" devolvem o mesmo `null` de propósito: **para quem
 * aplica**, os dois são "não faça nada". A diferença entre eles é para quem
 * PERGUNTA, e é `deveperguntar` que a lê.
 */
export function categoriaAutomatica(
  description: string,
  regras: readonly CategoryRule[],
  escopo: Exclude<RuleScope, 'both'> = 'transaction',
): { categoryId: string; ruleId: string } | null {
  const regra = regraQueCasa(chaveDaDescricao(description), regras, escopo)
  if (regra === null || regra.decision.kind !== 'category') return null
  return { categoryId: regra.decision.categoryId, ruleId: regra.id }
}

/**
 * Perguntar "vale sempre?" depois de alguém categorizar à mão?
 *
 * **Não**, quando já existe regra para a chave — seja com categoria (já se
 * sabe) ou `never` (já se decidiu que não). E não, quando a chave é vazia: uma
 * regra que casa com toda descrição só de números não é uma regra.
 *
 * O dono escolheu perguntar em vez de aprender em silêncio: a regra que nasce
 * sozinha só é descoberta quando já categorizou algo errado.
 */
export function devePerguntar(
  description: string,
  regras: readonly CategoryRule[],
  escopo: Exclude<RuleScope, 'both'> = 'transaction',
): boolean {
  const chave = chaveDaDescricao(description)
  if (chave === '') return false
  return regraQueCasa(chave, regras, escopo) === null
}

/**
 * De onde veio a categoria de uma transação.
 *
 * **`null` é "de antes desta fase", e lê como manual.** As 266 já categorizadas
 * da casa foram decididas por gente, e virar uma fila de conferência de 266
 * itens no dia do deploy seria transformar trabalho feito em trabalho por
 * fazer.
 */
export type CategorySource = {
  kind: 'manual' | 'auto'
  ruleId: string | null
  /** Quando alguém conferiu uma automática. `null` = ainda por conferir. */
  confirmedAt: string | null
}

/** A categoria foi posta pelo app e ainda não passou por olho humano? */
export function esperaConferencia(source: CategorySource | null): boolean {
  return source !== null && source.kind === 'auto' && source.confirmedAt === null
}
